import React, { useState } from "react";
import ServerCard from "./ServerCard";
import { svStatus } from "../../utils/format";
import { API_BASE } from "../../config";

export default function ServerMetricsView({
  servers,
  pm2Servers = [],
  alertCount,
  connectError,
  canEdit,
  onAdd,
  onEdit,
  onDelete,
  searchTerm = "",
}) {
  const [filter, setFilter] = useState("all"); // all | online | warn | offline

  const term = searchTerm.trim().toLowerCase();
  const list = Object.values(servers).filter((sv) => {
    if (!term) return true;
    const code = (sv.ServerCode || "").toLowerCase();
    const name = (sv.ServerName || "").toLowerCase();
    const ip = (sv.IP || "").toLowerCase();

    const hasMatchingApp = (sv.apps || []).some((app) =>
      (app.appName || "").toLowerCase().includes(term)
    );

    const pm2Srv = pm2Servers.find((s) => s.serverCode === sv.ServerCode);
    const hasMatchingPm2 = pm2Srv && (pm2Srv.processes || []).some((p) =>
      (p.name || "").toLowerCase().includes(term)
    );

    return (
      code.includes(term) ||
      name.includes(term) ||
      ip.includes(term) ||
      hasMatchingApp ||
      hasMatchingPm2
    );
  });
  const online = list.filter((sv) => sv.IsOnline && sv.isConnected);
  const warn = list.filter((sv) => svStatus(sv) === "warn");
  const offline = list.filter((sv) => svStatus(sv) === "offline");

  const avg = (arr) =>
    arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
  const avgCpu = avg(online.map((sv) => sv.metrics?.cpuUsage || 0));
  const avgRam = avg(online.map((sv) => sv.metrics?.ramPct || 0));

  // Lọc danh sách máy chủ
  const filteredList = list.filter((sv) => {
    if (filter === "online") return sv.IsOnline && sv.isConnected;
    if (filter === "warn") return svStatus(sv) === "warn";
    if (filter === "offline") return svStatus(sv) === "offline";
    return true;
  });

  const healthyPct = list.length ? Math.round((online.length / list.length) * 100) : 100;

  return (
    <div>
      {/* Filter and Actions Bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "18px",
        flexWrap: "wrap",
      }}>
        <div style={{
          display: "flex",
          padding: "3px",
          borderRadius: "10px",
          background: "var(--surface2)",
          border: "1px solid var(--border)",
        }}>
          <button
            onClick={() => setFilter("all")}
            style={{
              padding: "6px 14px",
              borderRadius: "7px",
              fontSize: "12.5px",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              background: filter === "all" ? "var(--primary)" : "transparent",
              color: filter === "all" ? "#fff" : "var(--dim)",
              transition: "all 0.15s ease",
            }}
          >
            All · {list.length}
          </button>
          <button
            onClick={() => setFilter("online")}
            style={{
              padding: "6px 14px",
              borderRadius: "7px",
              fontSize: "12.5px",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              background: filter === "online" ? "var(--primary)" : "transparent",
              color: filter === "online" ? "#fff" : "var(--dim)",
              transition: "all 0.15s ease",
            }}
          >
            Online {online.length}
          </button>
          <button
            onClick={() => setFilter("warn")}
            style={{
              padding: "6px 14px",
              borderRadius: "7px",
              fontSize: "12.5px",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              background: filter === "warn" ? "var(--primary)" : "transparent",
              color: filter === "warn" ? "#fff" : "var(--dim)",
              transition: "all 0.15s ease",
            }}
          >
            Warning {warn.length}
          </button>
          <button
            onClick={() => setFilter("offline")}
            style={{
              padding: "6px 14px",
              borderRadius: "7px",
              fontSize: "12.5px",
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              background: filter === "offline" ? "var(--primary)" : "transparent",
              color: filter === "offline" ? "#fff" : "var(--dim)",
              transition: "all 0.15s ease",
            }}
          >
            Offline {offline.length}
          </button>
        </div>
        
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          {canEdit && (
            <button
              onClick={onAdd}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                padding: "0 16px",
                height: "38px",
                borderRadius: "10px",
                background: "var(--primary)",
                border: "none",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 6px 16px -6px var(--primary-soft)",
                transition: "all 0.2s ease",
              }}
              className="add-server-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add server
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
        gap: "14px",
        marginBottom: "20px",
      }} className="overview-stats-grid">
        {/* Total Servers */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Servers</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="7" rx="2"></rect>
              <rect x="2" y="14" width="20" height="7" rx="2"></rect>
              <line x1="6" y1="6.5" x2="6.01" y2="6.5"></line>
              <line x1="6" y1="17.5" x2="6.01" y2="17.5"></line>
            </svg>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--text)" }}>{list.length}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>registered fleet</div>
        </div>

        {/* Online Servers */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Online</span>
            <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 8px var(--green)" }}></span>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--green)" }}>{online.length}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>{healthyPct}% healthy</div>
        </div>

        {/* Warnings */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Warnings</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--amber)" }}>{warn.length}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>needs attention</div>
        </div>

        {/* Average CPU */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Avg CPU</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="5" width="14" height="14" rx="2"></rect>
              <rect x="9" y="9" width="6" height="6"></rect>
              <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2"></path>
            </svg>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--text)" }}>{avgCpu}</span>
            <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--dim)" }}>%</span>
          </div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>active fleet</div>
        </div>

        {/* Average RAM */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Avg RAM</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h18v8H3z"></path>
              <path d="M7 8V6M12 8V6M17 8V6M7 20v-4M17 20v-4"></path>
            </svg>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: "var(--text)" }}>{avgRam}</span>
            <span style={{ fontSize: "16px", fontWeight: 600, color: "var(--dim)" }}>%</span>
          </div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>allocated RAM</div>
        </div>

        {/* Alerts */}
        <div style={{ padding: "16px", borderRadius: "14px", background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
            <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "var(--faint)" }}>Alerts</span>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={alertCount > 0 ? "var(--red)" : "var(--dim)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
            </svg>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "30px", fontWeight: 700, lineHeight: 1, color: alertCount > 0 ? "var(--red)" : "var(--text)" }}>{alertCount}</div>
          <div style={{ fontSize: "12px", color: "var(--dim)", marginTop: "6px" }}>active logs</div>
        </div>
      </div>

      {/* Grid of Server Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
        gap: "16px",
      }}>
        {filteredList.length === 0 ? (
          <div style={{ gridColumn: "1 / -1" }}>
            {connectError ? (
              <div className="empty-state" style={{ color: "var(--red)" }}>
                Không kết nối được BackendNode tại {API_BASE}
                <br />
                <span style={{ fontSize: 11, color: "var(--faint)", marginTop: 4 }}>
                  Kiểm tra server đang chạy và CORS
                </span>
              </div>
            ) : (
              <div className="empty-state">
                Không tìm thấy máy chủ nào thỏa mãn bộ lọc.
              </div>
            )}
          </div>
        ) : (
          filteredList.map((sv) => (
            <ServerCard
              key={sv.ServerCode}
              sv={sv}
              canEdit={canEdit}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
      
      <style>{`
        .add-server-btn:hover {
          filter: brightness(1.07);
        }
        @media (max-width: 1200px) {
          .overview-stats-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 640px) {
          .overview-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
}
